import { GroupAction } from '@assets/icons';
import AuditsTable from '@components/auditsTable/AuditsTable';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import { Colors } from '@constants/colors.constants';
import { alpha, Button, CardActionArea, Stack, Typography } from '@mui/material';
import { GridValueGetterParams } from '@mui/x-data-grid-pro';
import { useEffectFn } from '@ngneat/effects-hooks';
import { useObservable } from '@ngneat/react-rxjs';
import ContainerDetails from '@screens/admin/audits/audits-offer-formalization/ContainerDetails';
import EngagedPartnerModal from '@screens/admin/audits/audits-offer-formalization/EngagedPartnerModal';
import GroupActionModal from '@screens/admin/audits/audits-offer-formalization/GroupActionModal';
import GroupModal from '@screens/admin/audits/audits-offer-formalization/GroupModal';
import SelectSupplier from '@screens/admin/audits/audits-offer-formalization/SelectSupplier';
import SelectSupplierGrouped from '@screens/admin/audits/audits-offer-formalization/SelectSupplierGrouped';
import { AuditOfferGroupedDto, AuditOfferResponseExpendedDto } from '@store/audits/audit-offer/audit-offer.model';
import { auditsOfferService } from '@store/audits/audit-offer/audit-offer.services';
import { AuditsOfferFormalizationEffect } from '@store/audits/audit-offer/audits-offer.effects';
import { ServiceKindTypeEnum } from '@store/settings/serviceKinds';
import { NumberExt } from '@utils/extensions/number.extension';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import { finalize, switchMap } from 'rxjs';
import { auditsofferQuery } from '../../../../../store/audits/audit-offer/audits-offer.query';


const styleButtonPencil = {
  width: 'fit-content',
  padding: '10px',
  borderRadius: '4px',
  border: `1px solid ${alpha(Colors.primary, 0.5)}`,
  ':hover': {
    border: `1px solid ${Colors.primary}`,
    color: Colors.primary
  },
  ':active': {
    color: 'inherit'
  }
};


export interface EngagedSupplierData {
  id: number;
  supplier: string;
  phone: string;
  email: string;
  city: string;
  country: string;
  supplierNbr: number;
}


enum ActionType {
  SELECTSUPPLIER = 'selectSupplier',
  MULTISELECTSUPPLIER = 'multiSelectSupplier',
  GROUPEMODAL = 'groupModal',
  GROUPEDACTIONMODAL = 'groupActionModal',
  ENGAGEDPARTNERMODAL = 'engagedPartnerModal',
  CONTAINERDETAILS = 'containerDetails'
}


const AuditsOfferFormalization = () => {
  const { t } = useTranslation();
  const [Opentab, setOpentab] = useState<ActionType>();
  const handleCloseModal = () => setOpentab(undefined);
  const [selectedProposalRow, setSelectedProposalRow] = useState<number[]>([]);
  const [selectContainerId, setSelectContainerId] = useState<number>();
  const [loading, setLoading] = useState<boolean>(true);
  const [{ auditsOffers }] = useObservable(auditsofferQuery.auditsoffers$);
  const [{ engagedSuppliers }] = useObservable(auditsofferQuery.engagedSuppliers$);
  const { enqueueSnackbar } = useSnackbar();
  const { auditId } = useParams();
  const [selectedRowId, setSelectedRowId] = useState<number>();
  const [selectedOfferId, setSelectedOfferId] = useState<number>();
  const [selectedOffers, setSelectedOffers] = useState<AuditOfferResponseExpendedDto[]>();


  const auditOfferFiltered = auditsOffers?.sort((a, b) => b.auditOperationId - a.auditOperationId);
  const offerAction = useEffectFn(AuditsOfferFormalizationEffect);
  const [actionObj] = useObservable(auditsofferQuery.auditsFormalizationAction$);

  useEffect(() => {
    if (auditId) {
      setLoading(true);
      auditsOfferService.GETAuditOffersByAudit(auditId)
        .pipe(
          finalize(() => setLoading(false)),
          switchMap(() => auditsOfferService.GETSelectedSuppliersByAuditId(auditId as string))
        )
        .subscribe({
          error: (err) => {
            enqueueSnackbar(err.text, err.options);
          }
        });
    }
  }, [auditId, enqueueSnackbar]);

  useEffect(() => {
    if (auditId && actionObj.auditOfferIds.length > 0) {
      offerAction({ auditsFormalizationAction: actionObj, auditId });
    }
  }, [actionObj, auditId, offerAction]);

  const handleSelectedRows = (params: any) => {
    setSelectedProposalRow([...params]);
  };
  const handleSelectedDetails = (id: number) => {
    setSelectContainerId(id);
    setOpentab(ActionType.CONTAINERDETAILS);
  };

  const handleOpenSingleSupplier = (auditOfferId, selectedOfferId) => {
    setSelectedRowId(auditOfferId);
    setSelectedOfferId(selectedOfferId);
    setOpentab(ActionType.SELECTSUPPLIER);
  };


  const getCurrenModal = () => {
    return {
      [ActionType.SELECTSUPPLIER]: <SelectSupplier handleCloseModal={handleCloseModal} auditOfferId={selectedRowId} selectedOfferSupplierId={selectedOfferId} />,
      [ActionType.MULTISELECTSUPPLIER]: <SelectSupplierGrouped auditOffersIds={selectedProposalRow} handleCloseModal={handleCloseModal} selectedOffers={selectedOffers} />,
      [ActionType.GROUPEMODAL]: <GroupModal handleCloseModal={handleCloseModal} selectedProposalRow={selectedProposalRow} />,
      [ActionType.GROUPEDACTIONMODAL]: <GroupActionModal handleCloseModal={handleCloseModal} />,
      [ActionType.ENGAGEDPARTNERMODAL]: <EngagedPartnerModal data={engagedSuppliers} handleCloseModal={handleCloseModal} />,
      [ActionType.CONTAINERDETAILS]: <ContainerDetails onClose={handleCloseModal} auditOperationId={selectContainerId} />
    };
  };

  const ViewCurrentModal: JSX.Element = useMemo(() =>
    getCurrenModal()[Opentab as ActionType],
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [Opentab]
  );

  const handleExportCSV = () => {
    if (!auditId) return;
    auditsOfferService.getAuditOfferByAuditCSV(auditId)
      .pipe()
      .subscribe({
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
  };

  return (
    <Stack flex={1} width="100%" py={'20px'} spacing={5} sx={{ backgroundColor: 'white' }} mt='20px' borderRadius='2px' mb='20px'>
      <Stack direction={'row'} spacing={2} alignItems={'center'} justifyContent='space-between' px={'20px'} >
        <Typography fontSize="20px" fontWeight="700" flex={1}>
          {t('audits.global.OfferFormalization.title')}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setOpentab(ActionType.GROUPEDACTIONMODAL)}
            startIcon={<GroupAction />}
          >
            {t('global.groupedAction')}
          </Button>
          <Button variant="contained" color="primary"
            onClick={() => setOpentab(ActionType.ENGAGEDPARTNERMODAL)}
            disabled={!engagedSuppliers?.length}>
            {t('audits.buttons.engagedPartner', { partnerNbr: engagedSuppliers?.length })}
          </Button>
          <Button variant="contained" color="primary">
            {t('audits.buttons.extract-offer-pdf')}
          </Button>
          <Button variant="contained" color="primary" onClick={handleExportCSV}>
            {t('audits.buttons.extract-offer-csv')}
          </Button>
        </Stack>
      </Stack>
      <Stack spacing={2}>
        <Stack direction='row' px={'20px'} spacing={2}>
          <Button
            variant="outlined"
            color="primary"
            disabled={selectedProposalRow.length === 0}
            onClick={() => {
              let offersArray: AuditOfferResponseExpendedDto[] = [];
              auditOfferFiltered?.forEach((offerGroup: AuditOfferGroupedDto) => {
                const offers = offerGroup.auditOffers;
                offersArray = [...offersArray, ...offers];
              });
              const selectedOfferArray = offersArray.filter((offer) => selectedProposalRow.includes(offer.id));
              setSelectedOffers(selectedOfferArray);
              setOpentab(ActionType.MULTISELECTSUPPLIER);
            }}
          >
            {t('audits.OfferFormalization.selectSupplier.title')}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            disabled={selectedProposalRow.length === 0}
            onClick={() => setOpentab(ActionType.GROUPEMODAL)}
            startIcon={<GroupAction />}
          >
            {t('audits.OfferFormalization.groupedModal.title')}
          </Button>
        </Stack>
        <AuditsTable
          columns={[
            COLUMN_TYPES.TEXT({
              field: 'serviceType',
              label: 'Famille',
              valueFormatter: (value) => ServiceKindTypeEnum.selectItems().find((o) => o.value === value)?.label
            }),
            COLUMN_TYPES.TEXT({
              field: 'serviceFamily',
              label: 'Prestation',
            }),
            COLUMN_TYPES.TEXT({
              field: 'unitCostInCents',
              label: 'Prix partenaire',
              width: 20,
              valueGetter: (params: GridValueGetterParams<any, AuditOfferResponseExpendedDto>) => params.row.unitCostInCents,
              valueFormatter: (value) => value / 100,
            }),
            COLUMN_TYPES.CUSTOM({
              field: 'marginInPercent',
              label: 'Marge',
              valueFormatter: (value: number) => value ?? 0,
              renderCell: (params) => {
                const row = params.row as AuditOfferResponseExpendedDto;

                return (
                  <NumberTextFieldComponent
                    pattern={new RegExp(/^-?(\d*|\d+)[,.]?\d*$/)}
                    size={params.value ?? 'small'}
                    placeholder="-"
                    handleChange={(newValue) => auditsOfferService.setActionFormalization({ marginInPercent: newValue, unitPriceInCents: undefined, auditOfferIds: [row.id] })}
                    value={NumberExt.round(params.formattedValue, 2)}
                    endAdornment={<Typography>%</Typography>}
                    inputColor={params.inputColor?.(params.row)} />
                );
              },
              width: 20,
            }),
            COLUMN_TYPES.TEXT({
              label: 'Prix client',
              field: 'unitPriceInCents',
              valueGetter: (params: GridValueGetterParams<any, AuditOfferResponseExpendedDto>) => params.row.unitPriceInCents,
              valueFormatter: (value) => value / 100,
              renderCell: (params) => {
                const row = params.row as AuditOfferResponseExpendedDto;

                return (
                  <NumberTextFieldComponent
                    pattern={new RegExp(/^-?(\d*|\d+)[,.]?\d*$/)}
                    size={params.value ?? 'small'}
                    placeholder="-"
                    handleChange={(newValue) => auditsOfferService.setActionFormalization({ unitPriceInCents: newValue as number * 100, marginInPercent: undefined, auditOfferIds: [row.id] })}
                    value={params.formattedValue}
                    endAdornment={<Typography>CHF</Typography>}
                    inputColor={params.inputColor?.(params.row)} />
                );
              },
              width: 50
            }),
            COLUMN_TYPES.CUSTOM({
              label: 'Partenaire retenu',
              field: 'selectedSupplierName',
              renderCell: (params) => {
                return params.value ? (
                  <Stack direction="row" spacing={2} alignItems='center' justifyContent='end'>
                    <Typography fontSize={'14px'} fontWeight={400} >
                      {params.value}
                    </Typography>
                    <Stack component={CardActionArea} sx={styleButtonPencil}
                      onClick={() => handleOpenSingleSupplier(params.row.id, params.row.selectedOfferId)}
                    >
                      <GroupAction />
                    </Stack>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={2} alignItems='center' justifyContent='end'>
                    <Button variant="outlined" color="primary"
                      onClick={() => handleOpenSingleSupplier(params.row.id, params.row.selectedOfferId)}
                    >
                      <Typography fontSize={'14px'} fontWeight={400} noWrap>
                        Sélectionner un partenaire
                      </Typography>
                    </Button>
                  </Stack>
                );
              },
            })
          ]}
          loading={loading}
          handleSelectedRows={handleSelectedRows}
          selectedRows={selectedProposalRow}
          enableContainerDetails
          groupedBy='auditOperationId'
          handleSelectedDetails={handleSelectedDetails}
          data={auditOfferFiltered || []}
        />
      </Stack>
      {ViewCurrentModal}
      <Outlet />
    </Stack >
  );
};

export default AuditsOfferFormalization;
